import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { SharedValue, useSharedValue } from 'react-native-reanimated'
import { NoraVoiceModeSource } from '@src/screens/Nora/constants'

interface NoraVoiceModeContextValue {
  enabled: boolean
  setEnabled: (next: boolean, source?: NoraVoiceModeSource) => void
  endedSession: NoraVoiceModeEndedSession | null
  setEndedSession: (next: NoraVoiceModeEndedSession | null) => void
  muted: boolean
  setMuted: (next: boolean) => void
  source?: NoraVoiceModeSource
  transcript: string
  setTranscript: (next: string) => void
}

interface NoraVoiceModeVisualContextValue {
  enabled: boolean
  muted: boolean
  // Who is currently talking. Published here rather than returned from
  // useVoiceMode so the background animation can read it without instantiating
  // a second mic/playback session.
  speakingState: NoraSpeakingState
  setSpeakingState: (next: NoraSpeakingState) => void
  // Live 0..1 mic loudness. A shared value rather than React state so the ~20Hz
  // native level events never trigger a re-render — the animation reads it
  // directly on the UI thread.
  inputLevel: SharedValue<number>
}

interface NoraVoiceModeVisualActionsContextValue {
  setSpeakingState: (next: NoraSpeakingState) => void
  inputLevel: SharedValue<number>
}

export interface NoraSpeakingState {
  isSpeaking: boolean
  userSpeaking: boolean
}

export interface NoraVoiceModeEndedSession {
  id: string
  conversationId?: string
  durationSeconds: number
  source?: NoraVoiceModeSource
}

const IDLE_SPEAKING_STATE: NoraSpeakingState = { isSpeaking: false, userSpeaking: false }

const NoraVoiceModeContext = createContext<NoraVoiceModeContextValue | null>(null)
const NoraVoiceModeVisualContext = createContext<NoraVoiceModeVisualContextValue | null>(null)
const NoraVoiceModeVisualActionsContext = createContext<NoraVoiceModeVisualActionsContextValue | null>(
  null,
)

const StableVoiceModeChildren = React.memo(
  ({ children }: { children: ReactNode }): ReactNode => children,
)
StableVoiceModeChildren.displayName = 'StableVoiceModeChildren'

export const NoraVoiceModeProvider = ({
  children,
  initialEnabled = false,
  initialSource,
}: {
  children: ReactNode
  initialEnabled?: boolean
  initialSource?: NoraVoiceModeSource
}) => {
  const [enabled, setEnabledState] = useState(initialEnabled)
  const [muted, setMuted] = useState(false)
  const [endedSession, setEndedSession] = useState<NoraVoiceModeEndedSession | null>(null)
  const [source, setSource] = useState<NoraVoiceModeSource | undefined>(
    initialEnabled ? initialSource : undefined,
  )
  const [transcript, setTranscript] = useState('')
  const [speakingState, setSpeakingStateValue] = useState<NoraSpeakingState>(IDLE_SPEAKING_STATE)
  const inputLevel = useSharedValue(0)

  // Skip the state update when neither flag actually changed: useVoiceMode
  // recomputes this on every render, including the frequent partial-transcript
  // ones, and an unguarded set would re-render the whole voice tree each time.
  const setSpeakingState = useCallback((next: NoraSpeakingState) => {
    setSpeakingStateValue((current) =>
      current.isSpeaking === next.isSpeaking && current.userSpeaking === next.userSpeaking
        ? current
        : next,
    )
  }, [])

  const setEnabled = useCallback((next: boolean, nextSource?: NoraVoiceModeSource) => {
    setEnabledState(next)
    setSource(next ? nextSource : undefined)
    if (next) {
      setEndedSession(null)
    }
    if (!next) {
      setMuted(false)
      setTranscript('')
      setSpeakingStateValue(IDLE_SPEAKING_STATE)
    }
  }, [])

  const value = useMemo<NoraVoiceModeContextValue>(
    () => ({
      enabled,
      endedSession,
      setEnabled,
      setEndedSession,
      muted,
      setMuted,
      source,
      transcript,
      setTranscript,
    }),
    [enabled, endedSession, muted, setEnabled, source, transcript],
  )
  const visualValue = useMemo<NoraVoiceModeVisualContextValue>(
    () => ({
      enabled,
      muted,
      speakingState,
      setSpeakingState,
      inputLevel,
    }),
    [enabled, muted, speakingState, setSpeakingState, inputLevel],
  )
  const visualActionsValue = useRef<NoraVoiceModeVisualActionsContextValue>({
    setSpeakingState,
    inputLevel,
  }).current

  return (
    <NoraVoiceModeContext.Provider value={value}>
      <NoraVoiceModeVisualContext.Provider value={visualValue}>
        <NoraVoiceModeVisualActionsContext.Provider value={visualActionsValue}>
          <StableVoiceModeChildren>{children}</StableVoiceModeChildren>
        </NoraVoiceModeVisualActionsContext.Provider>
      </NoraVoiceModeVisualContext.Provider>
    </NoraVoiceModeContext.Provider>
  )
}

// Stand-in for consumers rendered outside the provider. Writes go nowhere,
// which matches the no-op setters below; a hook cannot be used here because the
// fallback is built inline during render.
const INERT_INPUT_LEVEL = { value: 0 } as SharedValue<number>

export const useNoraVoiceModeContext = () => {
  const contextValue = useContext(NoraVoiceModeContext) as NoraVoiceModeContextValue | null

  return (
    contextValue ?? {
      enabled: false,
      endedSession: null,
      muted: false,
      source: undefined,
      transcript: '',
      setEnabled: () => {
        // no-op when the provider isn't mounted
      },
      setEndedSession: () => {
        // no-op when the provider isn't mounted
      },
      setMuted: () => {
        // no-op when the provider isn't mounted
      },
      setTranscript: () => {
        // no-op when the provider isn't mounted
      },
    }
  )
}

export const useNoraVoiceModeVisualContext = () => {
  const contextValue = useContext(NoraVoiceModeVisualContext)

  return (
    contextValue ?? {
      enabled: false,
      muted: false,
      inputLevel: INERT_INPUT_LEVEL,
      speakingState: IDLE_SPEAKING_STATE,
      setSpeakingState: () => {
        // no-op when the provider isn't mounted
      },
    }
  )
}

export const useNoraVoiceModeVisualActionsContext = () => {
  const contextValue = useContext(NoraVoiceModeVisualActionsContext)

  return (
    contextValue ?? {
      inputLevel: INERT_INPUT_LEVEL,
      setSpeakingState: () => {
        // no-op when the provider isn't mounted
      },
    }
  )
}
